import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { EmSignerService } from '../../shared/em-signer.service';
import { MyErrorStateMatcher } from 'src/app/auth/login/login.component';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-em-signer',
  templateUrl: './em-signer.component.html',
  styleUrls: ['./em-signer.component.scss']
})
export class EmSignerComponent implements OnInit, OnDestroy {
  emSignerForm: FormGroup;
  matcher = new MyErrorStateMatcher();
  isLoading = false;
  originalFormValue: any = null;
  hasChanges = false;
  private formSubscription: Subscription;
  hideSecretKey = true;
  hidePassword = true;

  constructor(
    private formBuilder: FormBuilder,
    private emSignerService: EmSignerService,
    private toast: ToastrService
  ) {
    this.emSignerForm = this.formBuilder.group({
      secretKey: ['', [Validators.required]],
      appName: ['', [Validators.required]],
      username: ['', [Validators.required]],
      password: ['', [Validators.required]]
    });

    // Subscribe to form value changes
    this.formSubscription = this.emSignerForm.valueChanges.subscribe(() => {
      this.checkFormChanges();
    });
  }

  ngOnInit() {
    this.loadConfig();
  }

  ngOnDestroy() {
    if (this.formSubscription) {
      this.formSubscription.unsubscribe();
    }
  }

  private checkFormChanges() {
    if (!this.originalFormValue) {
      this.hasChanges = false;
      return;
    }
    
    const currentValue = this.emSignerForm.value;
    this.hasChanges = Object.keys(currentValue).some(key => 
      currentValue[key] !== this.originalFormValue[key]
    );
  }

  loadConfig() {
    this.isLoading = true;
    this.emSignerService.getConfig().subscribe({
      next: (response: any) => {
        if (response && response.isSuccess && response.body) {
          try {
            const config = response.body;
            const formValue = {
              secretKey: config.secretKey || '',
              appName: config.appName || '',
              username: config.username || '',
              password: config.password || ''
            };
            
            this.emSignerForm.setValue(formValue);
            this.originalFormValue = { ...formValue };
            this.hasChanges = false;
          } catch (err) {
            this.toast.error('Error loading configuration');
          }
        } else {
          this.toast.warning('No configuration found');
        }
        this.isLoading = false;
      },
      error: (error) => {
        this.toast.error('Failed to load Em-Signer configuration');
        this.isLoading = false;
      }
    });
  }

  onSubmit() {
    if (this.emSignerForm.valid && this.hasChanges) {
      this.isLoading = true;
      this.emSignerService.saveConfig(this.emSignerForm.value).subscribe({
        next: () => {
          this.toast.success('Em-Signer configuration saved successfully');
          this.originalFormValue = { ...this.emSignerForm.value };
          this.hasChanges = false;
          this.isLoading = false;
        },
        error: (error) => {
          if(error.status === 409){
            this.toast.error('Em-Signer configuration already exists');
          }else{
            this.toast.error('Failed to save Em-Signer configuration');
          }
          this.isLoading = false;
        }
      });
    }
  }

  resetForm() {
    if (this.originalFormValue) {
      this.emSignerForm.setValue(this.originalFormValue);
    } else {
      this.emSignerForm.reset();
    }
    this.hasChanges = false;
  }

  toggleSecretKeyVisibility() {
    this.hideSecretKey = !this.hideSecretKey;
  }

  togglePasswordVisibility() {
    this.hidePassword = !this.hidePassword;
  }
}
